import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Calendar,
  Database,
  Eye,
  FileText,
  Flame,
  Gift,
  GitCompare,
  Globe,
  Home,
  Loader2,
  LogOut,
  MessageSquare,
  Moon,
  Newspaper,
  Quote,
  RefreshCw,
  Settings,
  Sun,
  Trash2,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useCasesAPI, assessmentsAPI, adminAPI } from '../services/api';
import { domains, difficulties, categories, impacts, useCasesDetailed } from '../data/mockData';
import {
  mockWorkshopBookings,
  mockPendingUseCases,
  mockHotLeads,
  scoreLabels,
  comparisonColors,
  adminAssessmentQuestions,
  getScoreColor,
  getMaturityLabel,
} from '../data/adminConstants';
import TombolaAdminTab from '../components/TombolaAdminTab';
import AnalyticsAdminTab from '../components/AnalyticsAdminTab';
import ErrorBoundary from '../components/ErrorBoundary';
import AdminModals from '../components/AdminModals';
import { AdminProvider } from '../contexts/AdminContext';
import { AdminThemeProvider, useAdminTheme } from '../contexts/AdminThemeContext';
import { useHubspotAdmin } from '../hooks/admin/useHubspotAdmin';
import { useSessionAdmin } from '../hooks/admin/useSessionAdmin';
import { useWorkshopAdmin } from '../hooks/admin/useWorkshopAdmin';
import { useContentAdmin } from '../hooks/admin/useContentAdmin';
import { useBetaAdmin } from '../hooks/admin/useBetaAdmin';
import { useUserAdmin } from '../hooks/admin/useUserAdmin';
import { useComparisonAdmin } from '../hooks/admin/useComparisonAdmin';
import { useAdminOps } from '../hooks/admin/useAdminOps';

// Lazy-loaded admin tab components (with webpackChunkName hints for better DevTools/caching)
const OverviewTab = lazy(() => import(/* webpackChunkName: "admin-overview" */ '../components/admin/OverviewTab'));
const QuickscansTab = lazy(() => import(/* webpackChunkName: "admin-quickscans" */ '../components/admin/QuickscansTab'));
const ComparisonTab = lazy(() => import(/* webpackChunkName: "admin-comparison" */ '../components/admin/ComparisonTab'));
const UsersTab = lazy(() => import(/* webpackChunkName: "admin-users" */ '../components/admin/UsersTab'));
const BetaTab = lazy(() => import(/* webpackChunkName: "admin-beta" */ '../components/admin/BetaTab'));
const SessionsTab = lazy(() => import(/* webpackChunkName: "admin-sessions" */ '../components/admin/SessionsTab'));
const SettingsTab = lazy(() => import(/* webpackChunkName: "admin-settings" */ '../components/admin/SettingsTab'));
const UseCasesTab = lazy(() => import(/* webpackChunkName: "admin-usecases" */ '../components/admin/UseCasesTab'));
const WorkshopsTab = lazy(() => import(/* webpackChunkName: "admin-workshops" */ '../components/admin/WorkshopsTab'));
const ContentTab = lazy(() => import(/* webpackChunkName: "admin-content" */ '../components/admin/ContentTab'));
const LeadsTab = lazy(() => import(/* webpackChunkName: "admin-leads" */ '../components/admin/LeadsTab'));
const PresentationTab = lazy(() => import(/* webpackChunkName: "admin-presentation" */ '../components/admin/PresentationTab'));
const WebsiteAnalyticsTab = lazy(() => import(/* webpackChunkName: "admin-website-analytics" */ '../components/admin/WebsiteAnalyticsTab'));
const BlogTab = lazy(() => import(/* webpackChunkName: "admin-blog" */ '../components/admin/BlogTab'));

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [adminToken, setAdminToken] = useState(null);
  const [useCases, setUseCases] = useState(useCasesDetailed);
  const [assessments, setAssessments] = useState([]);
  // Workshop Bookings (state + actions from hook)
  const workshopAdmin = useWorkshopAdmin();
  const {
    workshopBookings, workshopStats, workshopLoading,
    showAddBookingModal, setShowAddBookingModal,
    expandedBooking, setExpandedBooking,
    newBooking, setNewBooking,
    fetchWorkshopBookings, handleUpdateBookingStatus, handleDeleteBooking, handleCreateBooking,
  } = workshopAdmin;
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUseCase, setEditingUseCase] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    avgScore: 0,
    thisWeek: 0,
  });
  const [newUseCase, setNewUseCase] = useState({
    title: '',
    description: '',
    shortDescription: '',
    category: 'quick-wins',
    secondaryCategory: '',
    domain: 'recruitment',
    difficulty: 'low',
    impact: 'medium',
    impl_time: '4w',
    hoursSaved: 0,
    tools: [],
  });
  
  // Beta Feedback state
  
  // New state for invite codes and tester management
  
  // Users state
  // Beta system state
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  // Assessment List selection state (for delete in Quickscans tab)
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  
  // Content Management state
  // Content Management (state + actions from hook)
  const contentAdmin = useContentAdmin();
  const {
    testimonials, trustedCompanies, contentLoading,
    showTestimonialModal, setShowTestimonialModal,
    showCompanyModal, setShowCompanyModal,
    editingTestimonial, setEditingTestimonial,
    editingCompany, setEditingCompany,
    newTestimonial, setNewTestimonial,
    newCompany, setNewCompany,
    fetchContentData,
    handleCreateTestimonial, handleUpdateTestimonial, handleDeleteTestimonial,
    handleCreateCompany, handleUpdateCompany, handleDeleteCompany,
  } = contentAdmin;
  
  // Beta Feedback (state + actions from hook)
  const betaAdmin = useBetaAdmin();
  const {
    betaFeedback, betaStats, betaTrends, betaTesters, betaLoading,
    selectedFeedback, setSelectedFeedback,
    showDeleteFeedbackConfirm, setShowDeleteFeedbackConfirm,
    feedbackToDelete, setFeedbackToDelete,
    betaStatusFilter, setBetaStatusFilter,
    betaCategoryFilter, setBetaCategoryFilter,
    trendPeriod, setTrendPeriod, setBetaTrends,
    inviteCodes,
    showCreateCodeModal, setShowCreateCodeModal,
    showCreateTesterModal, setShowCreateTesterModal,
    newCodeData, setNewCodeData,
    betaSystemEnabled,
    adminResponseInput, setAdminResponseInput,
    respondingTo, setRespondingTo,
    newTesterData, setNewTesterData,
    codeLoading, testerLoading,
    fetchBetaData, fetchBetaSettings,
    handleToggleBetaSystem, handleAddAdminResponse,
    handleCreateCode, handleDeleteCode, handleToggleCode,
    handleCreateTester, handleDeleteTester, handleToggleTester,
    handleDeleteFeedback, handleUpdateFeedbackStatus,
    handleExportJSON, handleExportMarkdown,
  } = betaAdmin;

  // Users (state + actions from hook)
  const userAdmin = useUserAdmin();
  const {
    users, setUsers, userStats, usersLoading,
    selectedUser, setSelectedUser, userSearchTerm, setUserSearchTerm,
    showUserDetailModal, setShowUserDetailModal,
    showEditUserModal, setShowEditUserModal,
    editUserData, setEditUserData,
    userDetailStats, loadingUserDetail,
    showCreateUserModal, setShowCreateUserModal,
    newUserData, setNewUserData,
    creatingUser,
    fetchUsers, handleCreateUser, handleDeleteUser,
    handleViewUserDetail, handleEditUser, handleSaveUserEdit,
    userPage, userPageSize, userTotal, userHasMore,
    goToPage, changePageSize,
  } = userAdmin;

  // HubSpot Integration (state + actions from hook)
  const hubspotAdmin = useHubspotAdmin();
  const {
    hubspotStatus, hubspotLoading, hubspotTestResult,
    fetchHubspotStatus, createHubspotProperties, testHubspotSync,
  } = hubspotAdmin;
  
  // Anonymous Session Analytics (state + actions from hook)
  const sessionAdmin = useSessionAdmin();
  const {
    sessionStats, sessionsList, sessionsLoading, sessionStatusFilter, setSessionStatusFilter,
    dropOffAnalysis, selectedSessionsForDelete, setSelectedSessionsForDelete,
    fetchSessionData, handleDeleteSelectedSessions,
  } = sessionAdmin;

  // Assessment Comparison (state + derived + actions from hook)
  // Note: onAssessmentsDeleted keeps the parent `assessments` state in sync
  // after bulk-delete from the comparison tab, without leaking setState here.
  const comparisonAdmin = useComparisonAdmin((deletedIds) => {
    setAssessments((prev) => prev.filter((a) => !deletedIds.includes(a.id)));
  });
  const {
    selectedForComparison, comparisonDrillDown, setComparisonDrillDown,
    selectedCategory, setSelectedCategory,
    comparisonAnalysis, analysisLoading, analysisError,
    radarData, comparisonStats,
    toggleComparisonSelection, clearComparison,
    generateComparisonAnalysis, deleteSelectedAssessments,
    getAnswerValue, getCategoryScore,
    MAX_COMPARISON_ITEMS,
  } = comparisonAdmin;

  // Admin-wide ops: seed DB, delete all, CSV export
  const adminOps = useAdminOps(() => fetchData());
  const {
    showSeedConfirm, setShowSeedConfirm,
    showDeleteConfirm, setShowDeleteConfirm,
    seedLoading, deleteLoading, settingsMessage,
  } = adminOps;

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    setAdminToken(token);
    fetchData();
    fetchBetaData();
    fetchUsers();
    fetchBetaSettings();
    fetchContentData();
    fetchWorkshopBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Hide HubSpot chat widget on /admin routes
  useEffect(() => {
    document.body.classList.add('admin-route');

    // JS-based hide for HubSpot chat widget (CSS alone isn't reliable due to iframe injection timing)
    const hideHubspot = () => {
      const selectors = [
        '#hubspot-messages-iframe-container',
        '[id^="hubspot-messages"]',
        '[class*="hubspot-messages-iframe-container"]',
      ];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          el.style.setProperty('display', 'none', 'important');
          el.style.setProperty('visibility', 'hidden', 'important');
        });
      });
    };
    hideHubspot();
    // HubSpot iframe often injects AFTER mount; re-hide for 5s via interval
    const intervalId = setInterval(hideHubspot, 500);
    const timeoutId = setTimeout(() => clearInterval(intervalId), 5000);

    // Also try HubSpot API if available
    if (window.HubSpotConversations?.widget) {
      try { window.HubSpotConversations.widget.remove(); } catch (e) {}
    }

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      document.body.classList.remove('admin-route');
      // Restore visibility
      const selectors = [
        '#hubspot-messages-iframe-container',
        '[id^="hubspot-messages"]',
        '[class*="hubspot-messages-iframe-container"]',
      ];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          el.style.removeProperty('display');
          el.style.removeProperty('visibility');
        });
      });
      if (window.HubSpotConversations?.widget) {
        try { window.HubSpotConversations.widget.load(); } catch (e) {}
      }
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [useCasesData, assessmentsData, statsData] = await Promise.all([
        useCasesAPI.getAll(),
        assessmentsAPI.getAllWithAnswers().catch(() => []),  // Include answers for comparison feature
        assessmentsAPI.getStats().catch(() => ({ total: 0, this_week: 0, avg_score: 0 })),
      ]);
      setUseCases(useCasesData.length > 0 ? useCasesData : useCasesDetailed);
      setAssessments(assessmentsData);
      setStats({
        total: statsData.total || assessmentsData.length,
        completed: statsData.total || assessmentsData.length,
        avgScore: statsData.avg_score || 0,
        thisWeek: statsData.this_week || 0,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users data
  // Create new user
  // Fetch beta settings
  // Toggle beta system
  // Add admin response to feedback
  // Delete user
  // View user details with KPIs
  // Open edit user modal
  // Save user edits
  // Create new invite code
  // Delete invite code
  // Toggle invite code status
  // Create new beta tester
  // Delete beta tester
  // Toggle beta tester status
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  const handleDeleteUseCase = async (id) => {
    if (!window.confirm('Are you sure you want to delete this use case?')) return;
    try {
      await useCasesAPI.delete(id);
      setUseCases((prev) => prev.filter((uc) => uc.id !== id));
    } catch (error) {
      console.error('Failed to delete use case:', error);
    }
  };

  // Refs that always hold the latest state — solves stale closure in adminActions useMemo([], [])
  const newUseCaseRef = useRef(newUseCase);
  const editingUseCaseRef = useRef(editingUseCase);
  useEffect(() => { newUseCaseRef.current = newUseCase; }, [newUseCase]);
  useEffect(() => { editingUseCaseRef.current = editingUseCase; }, [editingUseCase]);

  const handleAddUseCase = async () => {
    try {
      const current = newUseCaseRef.current;
      // Derive domainLabel from selected domain value
      const domainLabel = (domains.find(d => d.value === current.domain) || {}).label || current.domain;
      const payload = {
        ...current,
        domainLabel,
        // Mirror shortDescription -> description if description is empty (compact form ux)
        description: current.description?.trim() || current.shortDescription || '',
        // Empty string clears secondary category (backend strips None, but accepts "")
        secondaryCategory: current.secondaryCategory || '',
        // Sanitize tools list (frontend sends Array<string>)
        tools: Array.isArray(current.tools)
          ? current.tools.filter(t => t && t.trim()).map(t => t.trim())
          : [],
        // Coerce hoursSaved to int (or null)
        hoursSaved: current.hoursSaved === '' || current.hoursSaved == null
          ? null
          : parseInt(current.hoursSaved, 10) || 0,
      };
      const created = await useCasesAPI.create(payload);
      setUseCases((prev) => [...prev, created]);
      setShowAddModal(false);
      setNewUseCase({
        title: '',
        description: '',
        shortDescription: '',
        category: 'quick-wins',
        secondaryCategory: '',
        domain: 'recruitment',
        difficulty: 'low',
        impact: 'medium',
        impl_time: '4w',
        hoursSaved: 0,
        tools: [],
      });
    } catch (error) {
      console.error('Failed to create use case:', error);
      alert('Fout bij aanmaken use case: ' + (error?.response?.data?.detail || error.message));
    }
  };

  const handleUpdateUseCase = async () => {
    try {
      const current = editingUseCaseRef.current;
      if (!current) return;
      const domainLabel = (domains.find(d => d.value === current.domain) || {}).label || current.domainLabel;
      const updated = await useCasesAPI.update(current.id, {
        title: current.title,
        description: current.description || current.shortDescription || '',
        shortDescription: current.shortDescription || '',
        category: current.category,
        secondaryCategory: current.secondaryCategory || '',
        domain: current.domain,
        domainLabel,
        difficulty: current.difficulty,
        impact: current.impact,
        hoursSaved: current.hoursSaved === '' || current.hoursSaved == null
          ? null
          : parseInt(current.hoursSaved, 10) || 0,
        tools: Array.isArray(current.tools)
          ? current.tools.filter(t => t && t.trim()).map(t => t.trim())
          : [],
      });
      setUseCases((prev) =>
        prev.map((uc) => (uc.id === current.id ? { ...uc, ...updated } : uc))
      );
      setEditingUseCase(null);
    } catch (error) {
      console.error('Failed to update use case:', error);
      alert('Fout bij updaten use case: ' + (error?.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAssessment = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze quickscan wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) return;
    try {
      await assessmentsAPI.delete(id);
      setAssessments((prev) => prev.filter((a) => a.id !== id));
      setSelectedAssessment(null);
    } catch (error) {
      console.error('Failed to delete assessment:', error);
      alert('Fout bij het verwijderen van de quickscan');
    }
  };

  const handleUpdateLeadStatus = async (assessmentId, newStatus) => {
    try {
      await assessmentsAPI.updateLeadStatus(assessmentId, newStatus);
      // Update local state
      setAssessments((prev) =>
        prev.map((a) => (a.id === assessmentId ? { ...a, lead_status: newStatus } : a))
      );
      // Also update selected assessment if it's the one being modified
      if (selectedAssessment && selectedAssessment.id === assessmentId) {
        setSelectedAssessment((prev) => ({ ...prev, lead_status: newStatus }));
      }
    } catch (error) {
      console.error('Failed to update lead status:', error);
      alert('Fout bij het updaten van lead status');
    }
  };

  const handleSeedDatabase = adminOps.handleSeedDatabase;
  const handleDeleteAllData = adminOps.handleDeleteAllData;

  // Fetch HubSpot status when settings tab is active
  useEffect(() => {
    if (activeTab === 'settings' && !hubspotStatus) {
      fetchHubspotStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch session data when sessions tab is active
  useEffect(() => {
    if (activeTab === 'sessions') {
      fetchSessionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sessionStatusFilter]);

  const exportCSV = adminOps.exportCSV;

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'assessments', label: 'Quickscans', icon: FileText },
    { id: 'compare', label: 'Vergelijking', icon: GitCompare },
    { id: 'presentation', label: 'Presentatie', icon: TrendingUp },
    { id: 'analytics', label: 'Use Case Analytics', icon: Activity },
    { id: 'website-analytics', label: 'Website Analytics', icon: Globe },
    { id: 'sessions', label: 'Sessie Analytics', icon: Eye },
    { id: 'usecases', label: 'Use Cases', icon: Flame },
    { id: 'tombola', label: 'Tombola', icon: Gift },
    { id: 'content', label: 'Content', icon: Quote },
    { id: 'blog', label: 'Blog', icon: Newspaper },
    { id: 'workshops', label: 'Workshop Bookings', icon: Calendar },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'beta', label: 'Beta Feedback', icon: MessageSquare },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Preload lazy tab chunks on hover for instant tab switches
  const preloadMap = {
    overview: () => import('../components/admin/OverviewTab'),
    assessments: () => import('../components/admin/QuickscansTab'),
    compare: () => import('../components/admin/ComparisonTab'),
    sessions: () => import('../components/admin/SessionsTab'),
    usecases: () => import('../components/admin/UseCasesTab'),
    content: () => import('../components/admin/ContentTab'),
    workshops: () => import('../components/admin/WorkshopsTab'),
    users: () => import('../components/admin/UsersTab'),
    beta: () => import('../components/admin/BetaTab'),
    settings: () => import('../components/admin/SettingsTab'),
    leads: () => import('../components/admin/LeadsTab'),
    presentation: () => import('../components/admin/PresentationTab'),
    'website-analytics': () => import('../components/admin/WebsiteAnalyticsTab'),
  };
  const handleTabHover = (id) => {
    const loader = preloadMap[id];
    if (loader) loader();
  };

  // Calculate score distribution (memoized — was triggering useMemo dep warning)
  const scoreDistribution = useMemo(() => ({
    earlyStage: assessments.filter((a) => a.overall_score < 40).length,
    developing: assessments.filter((a) => a.overall_score >= 40 && a.overall_score < 70).length,
    advanced: assessments.filter((a) => a.overall_score >= 70).length,
  }), [assessments]);

  // Get top use cases (memoized)
  const topUseCases = useMemo(
    () => [...useCases].sort((a, b) => b.votes - a.votes).slice(0, 4),
    [useCases]
  );

  // Filter assessments for search (memoized)
  const filteredAssessments = useMemo(
    () => assessments.filter(
      (a) =>
        a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.email?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [assessments, searchTerm]
  );

  // Assessment questions + score labels live in /data/adminConstants.js
  const assessmentQuestions = adminAssessmentQuestions;

  // Toggle selection for delete in Quickscans tab
  const toggleDeleteSelection = (assessment) => {
    setSelectedForDelete(prev => {
      const isSelected = prev.some(a => a.id === assessment.id);
      if (isSelected) {
        return prev.filter(a => a.id !== assessment.id);
      }
      return [...prev, assessment];
    });
  };

  // Select all visible assessments for delete
  const selectAllForDelete = (assessmentsList) => {
    const allIds = assessmentsList.map(a => a.id);
    const allSelected = assessmentsList.every(a => selectedForDelete.some(s => s.id === a.id));
    if (allSelected) {
      setSelectedForDelete(prev => prev.filter(a => !allIds.includes(a.id)));
    } else {
      setSelectedForDelete(prev => {
        const newSelection = [...prev];
        assessmentsList.forEach(a => {
          if (!newSelection.some(s => s.id === a.id)) {
            newSelection.push(a);
          }
        });
        return newSelection;
      });
    }
  };

  // Delete selected assessments from Quickscans tab
  const deleteSelectedFromList = async () => {
    if (selectedForDelete.length === 0) return;
    
    const count = selectedForDelete.length;
    const confirmMessage = count === 1 
      ? `Weet u zeker dat u deze quickscan wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`
      : `Weet u zeker dat u deze ${count} quickscans wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const deletePromises = selectedForDelete.map(a => assessmentsAPI.delete(a.id));
      await Promise.all(deletePromises);
      
      const deletedIds = selectedForDelete.map(a => a.id);
      setAssessments(prev => prev.filter(a => !deletedIds.includes(a.id)));
      setSelectedForDelete([]);
      
      alert(`${count} quickscan${count > 1 ? 's' : ''} succesvol verwijderd.`);
    } catch (error) {
      console.error('Failed to delete assessments:', error);
      alert('Er is iets misgegaan bij het verwijderen. Probeer het opnieuw.');
    }
  };

  // ============= ADMIN CONTEXT VALUES (split data vs actions for perf) =============
  // Data: state that changes frequently. Re-renders consumers on any change.
  const adminData = useMemo(() => ({
    adminToken,
    assessments, filteredAssessments, stats, scoreDistribution, topUseCases,
    users, userStats, usersLoading, userSearchTerm, selectedUser,
    userDetailStats, loadingUserDetail,
    showUserDetailModal, showEditUserModal, showCreateUserModal,
    editUserData, newUserData, creatingUser,
    userPage, userPageSize, userTotal, userHasMore,
    betaFeedback, betaStats, betaTrends, betaTesters, betaLoading,
    betaStatusFilter, betaCategoryFilter, betaSystemEnabled,
    trendPeriod, respondingTo, adminResponseInput, inviteCodes,
    selectedFeedback, showDeleteFeedbackConfirm, showCreateCodeModal, showCreateTesterModal,
    newCodeData, newTesterData, codeLoading, testerLoading,
    selectedForComparison, comparisonDrillDown, selectedCategory,
    comparisonAnalysis, analysisLoading, analysisError,
    selectedForDelete, selectedAssessment, searchTerm,
    testimonials, trustedCompanies, contentLoading,
    showTestimonialModal, showCompanyModal, editingTestimonial, editingCompany,
    newTestimonial, newCompany,
    hubspotStatus, hubspotLoading, hubspotTestResult,
    sessionStats, sessionsList, sessionsLoading, sessionStatusFilter,
    dropOffAnalysis, selectedSessionsForDelete,
    workshopBookings, workshopStats, workshopLoading, expandedBooking,
    showAddBookingModal, newBooking,
    useCases, editingUseCase, showAddModal, newUseCase,
    settingsMessage, seedLoading, deleteLoading,
    showSeedConfirm, showDeleteConfirm,
    MAX_COMPARISON_ITEMS, comparisonColors, assessmentQuestions, scoreLabels, mockPendingUseCases, mockHotLeads,
    domains, difficulties, categories, impacts,
    radarData, comparisonStats,
  }), [
    adminToken, assessments, filteredAssessments, stats, scoreDistribution, topUseCases,
    users, userStats, usersLoading, userSearchTerm, selectedUser,
    userDetailStats, loadingUserDetail,
    showUserDetailModal, showEditUserModal, showCreateUserModal,
    editUserData, newUserData, creatingUser,
    userPage, userPageSize, userTotal, userHasMore,
    betaFeedback, betaStats, betaTrends, betaTesters, betaLoading,
    betaStatusFilter, betaCategoryFilter, betaSystemEnabled,
    trendPeriod, respondingTo, adminResponseInput, inviteCodes,
    selectedFeedback, showDeleteFeedbackConfirm, showCreateCodeModal, showCreateTesterModal,
    newCodeData, newTesterData, codeLoading, testerLoading,
    selectedForComparison, comparisonDrillDown, selectedCategory,
    comparisonAnalysis, analysisLoading, analysisError,
    selectedForDelete, selectedAssessment, searchTerm,
    testimonials, trustedCompanies, contentLoading,
    showTestimonialModal, showCompanyModal, editingTestimonial, editingCompany,
    newTestimonial, newCompany,
    hubspotStatus, hubspotLoading, hubspotTestResult,
    sessionStats, sessionsList, sessionsLoading, sessionStatusFilter,
    dropOffAnalysis, selectedSessionsForDelete,
    workshopBookings, workshopStats, workshopLoading, expandedBooking,
    showAddBookingModal, newBooking,
    useCases, editingUseCase, showAddModal, newUseCase,
    settingsMessage, seedLoading, deleteLoading,
    showSeedConfirm, showDeleteConfirm,
    radarData, comparisonStats,
    MAX_COMPARISON_ITEMS, assessmentQuestions,
  ]);

  // Actions: setters + handlers. Stable refs where possible (useCallback in hooks).
  // Memoized with empty deps because setters from useState are stable & handlers from hooks are useCallback-wrapped.
  const adminActions = useMemo(() => ({
    // Setters
    setAssessments,
    setUsers, setUserSearchTerm, setBetaStatusFilter, setBetaCategoryFilter, setBetaTrends,
    setTrendPeriod, setRespondingTo, setAdminResponseInput, setSelectedFeedback, setFeedbackToDelete,
    setShowDeleteFeedbackConfirm, setShowCreateCodeModal, setShowCreateTesterModal,
    setShowCreateUserModal, setShowAddModal, setShowAddBookingModal, setShowTestimonialModal,
    setShowCompanyModal, setShowSeedConfirm, setShowDeleteConfirm,
    setSelectedAssessment, setSearchTerm, setComparisonDrillDown, setSelectedCategory,
    setSelectedSessionsForDelete, setSessionStatusFilter, setExpandedBooking,
    setEditingTestimonial, setEditingCompany, setEditingUseCase, setUseCases, setNewUseCase,
    // Handlers
    handleToggleBetaSystem, handleAddAdminResponse, handleUpdateFeedbackStatus,
    handleExportJSON, handleExportMarkdown, handleDeleteCode, handleToggleCode,
    handleDeleteTester, handleToggleTester, fetchBetaData,
    handleDeleteSelectedSessions, fetchSessionData,
    handleDeleteBooking, handleUpdateBookingStatus, fetchWorkshopBookings,
    handleDeleteTestimonial, handleDeleteCompany, fetchContentData,
    handleDeleteUseCase, handleAddUseCase, handleUpdateUseCase,
    handleViewUserDetail, handleEditUser, handleDeleteUser, fetchUsers,
    goToPage, changePageSize,
    fetchHubspotStatus, createHubspotProperties, testHubspotSync,
    toggleComparisonSelection, deleteSelectedAssessments, clearComparison,
    generateComparisonAnalysis, toggleDeleteSelection, selectAllForDelete,
    deleteSelectedFromList,
    handleDeleteAssessment, handleUpdateLeadStatus,
    exportCSV, getCategoryScore, getAnswerValue, getScoreColor,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <AdminThemeProvider>
      <AdminProvider data={adminData} actions={adminActions}>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-blue-500" />
            <div>
              <span className="text-lg font-bold text-slate-900 dark:text-white">HR-AI Admin Dashboard</span>
              <p className="text-xs text-slate-500 dark:text-slate-400">Analytics & Lead Management</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveTab(item.id)}
                  onMouseEnter={() => handleTabHover(item.id)}
                  onFocus={() => handleTabHover(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === item.id
                      ? 'bg-blue-500/20 text-blue-500'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Theme toggle + Logout */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 space-y-1">
          <ThemeToggleButton />
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-auto">
        {/* Header Buttons */}
        <div className="flex justify-end gap-3 mb-6">
          <Button
            variant="outline"
            className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
            onClick={() => setShowSeedConfirm(true)}
            disabled={seedLoading}
          >
            {seedLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Database className="h-4 w-4 mr-2" />
            )}
            Seed Demo Data
          </Button>
          <Button
            variant="outline"
            className="border-green-500 text-green-400 hover:bg-green-500/20"
            onClick={fetchData}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500/20"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Delete All Data
          </Button>
          <Button
            variant="outline"
            className="border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            onClick={() => navigate('/')}
          >
            <Home className="h-4 w-4 mr-2" />
            Home
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <ErrorBoundary key={activeTab}>
            <Suspense fallback={
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            }>
            <>
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <OverviewTab />
            )}

            {/* ASSESSMENTS TAB */}
            {activeTab === 'assessments' && (
              <QuickscansTab />
            )}

            {/* COMPARISON TAB - Spider Diagram */}
            {activeTab === 'compare' && (
              <ComparisonTab />
            )}


            {/* SESSIONS ANALYTICS TAB */}
            {activeTab === 'sessions' && (
              <SessionsTab />
            )}

            {/* USE CASES TAB */}
            {activeTab === 'usecases' && (
              <UseCasesTab />
            )}

            {/* WORKSHOPS TAB */}
            {activeTab === 'workshops' && (
              <WorkshopsTab />
            )}

            {/* LEADS TAB */}
            {activeTab === 'leads' && (
              <LeadsTab />
            )}

            {/* USERS TAB */}
            {activeTab === 'users' && (
              <UsersTab />
            )}


            {/* BETA FEEDBACK TAB */}
            {activeTab === 'beta' && (
              <BetaTab />
            )}

            {/* CONTENT MANAGEMENT TAB */}
            {activeTab === 'content' && (
              <ContentTab />
            )}

            {/* ANALYTICS TAB */}
            {activeTab === 'analytics' && (
              <AnalyticsAdminTab />
            )}

            {/* WEBSITE ANALYTICS TAB */}
            {activeTab === 'website-analytics' && (
              <WebsiteAnalyticsTab />
            )}

            {/* PRESENTATION TAB */}
            {activeTab === 'presentation' && (
              <PresentationTab />
            )}

            {/* TOMBOLA TAB */}
            {activeTab === 'tombola' && (
              <TombolaAdminTab token={adminToken} />
            )}

            {/* SETTINGS TAB */}
            {activeTab === 'settings' && (
              <SettingsTab />
            )}

            {/* BLOG TAB */}
            {activeTab === 'blog' && (
              <BlogTab />
            )}
            </>
            </Suspense>
          </ErrorBoundary>
        )}
      </main>

      <AdminModals />
    </div>
    </AdminProvider>
    </AdminThemeProvider>
  );
};

// Small inline component — must be inside the AdminThemeProvider tree.
const ThemeToggleButton = () => {
  const { theme, toggleTheme } = useAdminTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      data-testid="admin-theme-toggle"
      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white transition-colors"
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      {isDark ? 'Light mode' : 'Dark mode'}
    </button>
  );
};

export default AdminDashboard;
